/**
 * Chemin critique par la méthode CPM (Critical Path Method), généralisée aux 4 types de
 * dépendance (FS/SS/FF/SF) avec lag signé. Fonctions pures, aucun accès DOM.
 *
 * Principe : tri topologique (Kahn) pour détecter un cycle sans jamais boucler à l'infini,
 * puis passe avant (dates au plus tôt) et passe arrière (dates au plus tard) exprimées en
 * jours écoulés depuis 0 — la sémantique du "quand" est portée par les durées et les lags,
 * pas par les dates calendaires déjà posées sur les tâches.
 */

import { diffDays } from './scale.js';

/**
 * Tri topologique de Kahn. Ne boucle jamais indéfiniment : si un cycle existe, les nœuds qui
 * n'atteignent jamais un in-degree nul restent hors de `order`, et les arêtes purement internes
 * à ce reliquat sont retournées comme `cycleEdges`.
 */
export function topologicalSort(taskIds, dependencies) {
  const inDegree = new Map(taskIds.map((id) => [id, 0]));
  const adjacency = new Map(taskIds.map((id) => [id, []]));

  for (const dep of dependencies) {
    if (!adjacency.has(dep.predecessorId) || !inDegree.has(dep.successorId)) continue;
    adjacency.get(dep.predecessorId).push(dep.successorId);
    inDegree.set(dep.successorId, inDegree.get(dep.successorId) + 1);
  }

  const queue = taskIds.filter((id) => inDegree.get(id) === 0);
  const order = [];

  while (queue.length > 0) {
    const id = queue.shift();
    order.push(id);
    for (const next of adjacency.get(id) ?? []) {
      inDegree.set(next, inDegree.get(next) - 1);
      if (inDegree.get(next) === 0) queue.push(next);
    }
  }

  if (order.length === taskIds.length) {
    return { order, cycleEdges: [] };
  }

  const stuck = new Set(taskIds.filter((id) => !order.includes(id)));
  const cycleEdges = dependencies.filter(
    (dep) => stuck.has(dep.predecessorId) && stuck.has(dep.successorId),
  );
  return { order, cycleEdges };
}

function taskDuration(task) {
  if (!task || task.kind === 'milestone') return 0;
  if (!task.startDate || !task.dueDate) return 0;
  return Math.max(diffDays(task.startDate, task.dueDate), 0);
}

/**
 * Calcule le chemin critique. En cas de cycle, renvoie `{ error: { type: 'cycle', edges } }`
 * (jamais de boucle infinie). Sinon, renvoie `{ order, tasks, projectEnd }` où `tasks` associe
 * chaque id à `{ es, ef, ls, lf, totalFloat, critical }` (marge totale nulle => `critical: true`).
 */
export function computeCriticalPath({ tasks, dependencies }) {
  const taskIds = tasks.map((t) => t.id);
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const { order, cycleEdges } = topologicalSort(taskIds, dependencies);

  if (cycleEdges.length > 0) {
    return { error: { type: 'cycle', edges: cycleEdges } };
  }

  const predsOf = new Map(taskIds.map((id) => [id, []]));
  const succsOf = new Map(taskIds.map((id) => [id, []]));
  for (const dep of dependencies) {
    if (!predsOf.has(dep.successorId) || !succsOf.has(dep.predecessorId)) continue;
    predsOf.get(dep.successorId).push(dep);
    succsOf.get(dep.predecessorId).push(dep);
  }

  // Passe avant : dates au plus tôt (ES/EF), en jours depuis 0.
  const es = new Map();
  const ef = new Map();
  for (const id of order) {
    const duration = taskDuration(byId.get(id));
    let earliestStart = 0;
    for (const dep of predsOf.get(id)) {
      const predEs = es.get(dep.predecessorId);
      const predEf = ef.get(dep.predecessorId);
      let bound;
      if (dep.type === 'FS') bound = predEf + dep.lagDays;
      else if (dep.type === 'SS') bound = predEs + dep.lagDays;
      else if (dep.type === 'FF') bound = predEf + dep.lagDays - duration;
      else bound = predEs + dep.lagDays - duration; // SF
      earliestStart = Math.max(earliestStart, bound);
    }
    es.set(id, earliestStart);
    ef.set(id, earliestStart + duration);
  }

  const projectEnd = order.length > 0 ? Math.max(...order.map((id) => ef.get(id))) : 0;

  // Passe arrière : dates au plus tard (LS/LF), en partant de la fin de projet.
  const ls = new Map();
  const lf = new Map();
  for (let i = order.length - 1; i >= 0; i -= 1) {
    const id = order[i];
    const duration = taskDuration(byId.get(id));
    const succs = succsOf.get(id);
    let latestFinish = succs.length === 0 ? projectEnd : Infinity;
    for (const dep of succs) {
      const succLs = ls.get(dep.successorId);
      const succLf = lf.get(dep.successorId);
      let bound;
      if (dep.type === 'FS') bound = succLs - dep.lagDays;
      else if (dep.type === 'SS') bound = succLs - dep.lagDays + duration;
      else if (dep.type === 'FF') bound = succLf - dep.lagDays;
      else bound = succLf - dep.lagDays + duration; // SF
      latestFinish = Math.min(latestFinish, bound);
    }
    lf.set(id, latestFinish);
    ls.set(id, latestFinish - duration);
  }

  const resultTasks = new Map();
  for (const id of order) {
    const totalFloat = ls.get(id) - es.get(id);
    resultTasks.set(id, {
      es: es.get(id),
      ef: ef.get(id),
      ls: ls.get(id),
      lf: lf.get(id),
      totalFloat,
      critical: totalFloat <= 0,
    });
  }

  return { order, tasks: resultTasks, projectEnd };
}
